import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'components/Form'
import { ConfirmationModal, Modal, SimpleTable, Tooltip } from 'components/Ui'
import {
    getApiData,
    deleteApiRecord,
    updateApiRecord,
    oneClickAuthorize,
    syncAllShopifyTasks,
} from 'services/ShopifyIntegrationService'
// import { Field, Form, Formik } from 'formik'
import { AiOutlinePlus } from 'react-icons/ai'
import { HiOutlinePencil, HiOutlineRefresh, HiOutlineTrash } from 'react-icons/hi'
import { Success } from 'utils/alerts'
import { activeStatus, fullfillmentStatus } from 'components/helper'
import Edit from './shopify/Edit'
import Create from './shopify/Create'

export default function ShopifyIntegrationTabView({ companyData, customersData, record }) {
    const [data, setData] = useState([])
    const [selectedData, setSelectedData] = useState()
    const [open, setOpen] = useState(false)
    const [syncLoading, setSyncLoading] = useState(false)
    const authProcessedRef = useRef(false)

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const successParam = params.get('success')
        const shopParam = params.get('shop')
        const errorParam = params.get('error')
        const storeIdParam = params.get('store_id')

        // Check if we have OAuth callback parameters and haven't processed them yet
        if ((successParam || errorParam) && !authProcessedRef.current) {
            // Mark as processed immediately to prevent duplicate processing
            authProcessedRef.current = true

            // Clear URL parameters to prevent reprocessing if page refreshes
            window.history.replaceState({}, document.title, window.location.pathname)

            // Handle success case
            if (successParam === 'true') {
                console.log(`Shopify authorization successful for shop: ${shopParam}, store ID: ${storeIdParam}`)
                Success('Shopify integration successful! Store connected.')
                getData({ company: record?.company?.id, customer: record?.id })
            }
            // Handle error case
            else if (errorParam) {
                console.error('Error during Shopify integration:', errorParam)
                Error(errorParam || 'Shopify integration failed!')
            }
        }

        // Reset the processed flag when URL params are empty (allows future auth attempts)
        if (!successParam && !errorParam) {
            authProcessedRef.current = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (record) {
            getData({ company: record?.company?.id, customer: record?.id })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const getData = data => {
        getApiData(data)
            .then(response => {
                if (response.data) {
                    setData(response.data?.results)
                }
            })
            .catch(error => console.log('Error fetching order type data: ', error))
    }

    const handleStatus = (id, data) => {
        delete data.image
        updateApiRecord(id, data)
            .then(() => {
                Success('Status updated successfully!')
                getData({
                    company: record?.company?.id,
                    customer: record?.id,
                })
            })
            .catch(error => {
                Error('Failed to update status!')
            })
    }

    const handleDelete = id => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                getData({
                    company: record?.company?.id,
                    customer: record?.id,
                })
            })
            .catch(error => console.log('Error: ', error))
    }

    const handleReauthorize = row => {
        // Prepare base data for authorization
        const data = {
            company_id: row.company?.id || row.company, // Extract ID if it's an object, otherwise use as is
            customer_id: row.customer?.id || row.customer || null, // Extract ID if it's an object, otherwise use as is
            shop_name: row.shop_domain.replace('.myshopify.com', ''), // Remove domain suffix if present
        }

        // let authPromise

        // Determine which authorization method to use based on integration type
        if (row.integration_type === 'custom_app') {
            // For custom app integrations, edit mode should be used instead of reauthorization
            Error('Custom app integrations cannot be reauthorized. Please edit the integration to update credentials.')
            return
        } else {
            // Use one-click authorization for one_click integration type
            oneClickAuthorize(data)
                .then(response => {
                    if (response.data.success && response.data.authorization_url) {
                        // Redirect to Shopify authorization page
                        window.location.href = response.data.authorization_url
                    } else {
                        Error('Failed to generate authorization URL')
                    }
                })
                .catch(error => {
                    console.error('Error initiating Shopify authorization:', error)
                    const errorMessage = error.response?.data?.error || 'Failed to connect to Shopify!'
                    Error(errorMessage)
                })
        }
    }

    const handleSyncAllTasks = () => {
        setSyncLoading(true)
        syncAllShopifyTasks()
            .then(response => {
                console.log('Sync response:', response.data)
                const { message, summary } = response.data
                Success(`${message}. Tasks: ${summary.successful}/${summary.total_tasks} successful`)
            })
            .catch(error => {
                console.error('Error syncing Shopify tasks:', error)
                const errorMessage = error.response?.data?.error || 'Failed to trigger sync tasks!'
                Error(errorMessage)
            })
            .finally(() => {
                setSyncLoading(false)
            })
    }

    return (
        <div>
            <div className="flex items-center justify-end mb-4">
                <div className="flex gap-2">
                    <Tooltip title="Sync all Shopify tasks (Orders, Products, Inventory, Fulfillments)">
                        <Button
                            variant="twoTone"
                            color="blue-600"
                            onClick={handleSyncAllTasks}
                            disabled={syncLoading}
                            className="flex items-center gap-2"
                        >
                            <HiOutlineRefresh className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
                            {syncLoading ? 'Syncing...' : 'Sync All'}
                        </Button>
                    </Tooltip>
                    <Button
                        className="text-white border-[#F05540] bg-[#F05540] dark:border-[#F05540] dark:bg-[#F05540]"
                        onClick={() => setOpen(true)}
                    >
                        <AiOutlinePlus size={20} /> Add New
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-4">
                <SimpleTable>
                    <thead>
                        <SimpleTable.TR>
                            <SimpleTable.TH>Shop Domain</SimpleTable.TH>
                            <SimpleTable.TH>Company</SimpleTable.TH>
                            <SimpleTable.TH>Customer</SimpleTable.TH>
                            <SimpleTable.TH>Integration Type</SimpleTable.TH>
                            <SimpleTable.TH>Status</SimpleTable.TH>
                            <SimpleTable.TH>Fulfillment Sync</SimpleTable.TH>
                            <SimpleTable.TH>Action</SimpleTable.TH>
                        </SimpleTable.TR>
                    </thead>
                    <tbody>
                        {(data || [])?.map((row, index) => (
                            <SimpleTable.TR key={`row-${index}`}>
                                <SimpleTable.TD>{row?.shop_domain}</SimpleTable.TD>
                                <SimpleTable.TD>{row.company?.code}</SimpleTable.TD>
                                <SimpleTable.TD>{row?.customer?.customer_code}</SimpleTable.TD>
                                <SimpleTable.TD>
                                    <span
                                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                                            row.integration_type === 'custom_app'
                                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        }`}
                                    >
                                        {row.integration_type === 'custom_app' ? 'Custom App' : 'One-Click'}
                                    </span>
                                </SimpleTable.TD>
                                <SimpleTable.TD>
                                    {/* {checkPermission('shopify-integration', 'status') ? ( */}
                                    <ConfirmationModal
                                        onConfirm={() => {
                                            handleStatus(row.id, { is_active: !row.is_active })
                                        }}
                                    >
                                        {activeStatus(row.is_active)}
                                    </ConfirmationModal>
                                    {/* ) : (
                                        activeStatus(row.is_active)
                                    )} */}
                                </SimpleTable.TD>
                                <SimpleTable.TD>
                                    {/* {checkPermission('company', 'status') ? ( */}
                                    <ConfirmationModal
                                        onConfirm={() => {
                                            handleStatus(row.id, { enable_fulfillment_sync: !row.enable_fulfillment_sync })
                                        }}
                                    >
                                        {fullfillmentStatus(row.enable_fulfillment_sync)}
                                    </ConfirmationModal>
                                    {/* ) : (
                                        fullfillmentStatus(row.enable_fulfillment_sync)
                                    )} */}
                                </SimpleTable.TD>
                                <SimpleTable.TD>
                                    <div className="flex items-center justify-center gap-3">
                                        {/* {checkPermission('shopify-integration', 'update') && */}
                                        row.integration_type !== 'custom_app' && (
                                        <Tooltip title="Re-authorize with Shopify">
                                            <div className="inline-block">
                                                {' '}
                                                {/* Wrapper div to avoid button nesting */}
                                                <Button
                                                    size="sm"
                                                    variant="twoTone"
                                                    color="emerald-600"
                                                    onClick={() => handleReauthorize(row)}
                                                    className="!px-2 !py-1"
                                                >
                                                    Connect
                                                </Button>
                                            </div>
                                        </Tooltip>
                                        {/* )} */}
                                        {/* {checkPermission('order-type', 'edit') ? ( */}
                                        <Tooltip title={'Edit'}>
                                            <span
                                                onClick={() => {
                                                    setSelectedData(row)
                                                    setOpen(true)
                                                }}
                                                className="hover:text-primary"
                                            >
                                                <HiOutlinePencil size={20} />
                                            </span>
                                        </Tooltip>
                                        {/* ) : null} */}
                                        {/* {checkPermission('order-type', 'delete') ? ( */}
                                        <Tooltip title={'Delete'}>
                                            <ConfirmationModal
                                                message={'Are you sure you want to delete this record!'}
                                                onConfirm={() => handleDelete(row.id)}
                                            >
                                                <span className="hover:text-danger">
                                                    <HiOutlineTrash size={20} />
                                                </span>
                                            </ConfirmationModal>
                                        </Tooltip>
                                        {/* ) : null} */}
                                    </div>
                                </SimpleTable.TD>
                            </SimpleTable.TR>
                        ))}
                    </tbody>
                </SimpleTable>
            </div>
            <Modal
                show={open}
                maxWidth="4xl"
                onClose={() => {
                    setOpen(false)
                    setSelectedData()
                }}
            >
                <div className="flex flex-col gap-1 m-3">
                    <h2 className="font-semibold text-black text-title-md">
                        {selectedData ? 'Update Shopify Integration' : 'Add New Shopify Integration'}
                    </h2>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white border rounded-sm overflow-auto max-h-150 border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                        <div className="flex flex-col gap-3 p-6">
                            {/* <Formik
                                initialValues={
                                    selectedData
                                        ? selectedData
                                        : { company: record?.company?.id, customer: record?.id, order_type_name: '' }
                                }
                                validationSchema={validationSchema}
                                onSubmit={values => {
                                    handleSubmit(values)
                                }}
                            >
                                {({ values, touched, errors }) => (
                                    <Form>
                                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-4">
                                            <Field name="company">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="company"
                                                        name="company"
                                                        label="Company"
                                                        placeholder="Company"
                                                        className="md:col-span-2"
                                                        value={values?.company}
                                                        options={companyData}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                        error={touched.company && errors.company}
                                                        message={errors.company}
                                                        disabled
                                                    />
                                                )}
                                            </Field>
                                            <Field name="customer">
                                                {({ field, form }) => (
                                                    <Select
                                                        id="customer"
                                                        name="customer"
                                                        label="Customer"
                                                        placeholder="Customer"
                                                        className="md:col-span-2"
                                                        value={values?.customer}
                                                        options={customersData}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                        error={touched.customer && errors.customer}
                                                        message={errors.customer}
                                                        disabled
                                                    />
                                                )}
                                            </Field>
                                            <Field name="order_type_name">
                                                {({ field, form }) => (
                                                    <TextInput
                                                        type="text"
                                                        className="md:col-span-2"
                                                        label="Name"
                                                        name="order_type_name"
                                                        error={errors.order_type_name && touched.order_type_name}
                                                        message={errors.order_type_name}
                                                        value={values?.order_type_name}
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <div className="flex items-center justify-end gap-4 mt-3">
                                            <Button
                                                className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                                type="button"
                                                onClick={() => {
                                                    setOpen(false)
                                                    setSelectedData()
                                                }}
                                            >
                                                Discard
                                            </Button>
                                            <Button
                                                type="submit"
                                                className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik> */}
                            {selectedData ? (
                                <Edit
                                    id={selectedData?.id}
                                    record={selectedData}
                                    setOpen={setOpen}
                                    setSelectedData={setSelectedData}
                                />
                            ) : (
                                <Create
                                    view={{ shop_name: '', company_id: record?.company?.id, customer_id: record?.id }}
                                    setOpen={setOpen}
                                    setSelectedData={setSelectedData}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
